import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Avatar,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Backdrop,
  CircularProgress,
  TextField
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  PersonOutline as PersonOutlineIcon,
  AccountBalanceWallet as CashIcon,
  AccountBalance as ExpenseIcon,
  PhoneAndroid as MobileIcon,
  CreditCard as CreditIcon,
  Receipt as ReceiptIcon,
  FilterList as FilterIcon,
  BarChart as BarChartIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { userTransaction } from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';
import { formatWithOutSom } from '../Message';

const Home = () => {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState('');

  const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(() => {
    return getLocalDateString();
  });

  const [endDate, setEndDate] = useState(() => {
    return getLocalDateString();
  });

  const [billingActivity, setBillingActivity] = useState([]);
  const [pettyExpenses, setPettyExpenses] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({ totalRevenue:0, totalReversal:0, totalExpenses:0, netRevenue:0 });

  // Each card uses a single accent color — backgrounds + icon colors are
  // derived via alpha() in the renderer so they read well on both themes.
  const [statCards, setStatCards] = useState([
    { title: 'Cash Transactions',     value: '0', subtitle: 'Total cash payments',         icon: 'CashIcon',         accent: '#10b981' },
    { title: 'Mobile Money',          value: '0', subtitle: 'Mobile payment transactions', icon: 'MobileIcon',       accent: '#3b82f6' },
    { title: 'Mobile Money(manual)',  value: '0', subtitle: 'Mobile payment transactions', icon: 'MobileIcon',       accent: '#ec4899' },
    { title: 'Reversed Transaction',  value: '0', subtitle: 'Transaction Amount Canceled', icon: 'ScheduleIcon',     accent: '#06b6d4' },
    { title: 'Credit Transactions',   value: '0', subtitle: 'Credit',                      icon: 'CreditIcon',       accent: '#f59e0b' },
    { title: 'Petty Expenses',        value: '0', subtitle: 'User petty expenses',         icon: 'ExpenseIcon',      accent: '#8b5cf6' },
  ]);

  useEffect(() => {
    const savedUserInfo = localStorage.getItem('userInfo');
    if (savedUserInfo) {
      const userInfo = JSON.parse(savedUserInfo);
      const userName = userInfo.name || '';
      console.log('Setting current user:', userName);
      setCurrentUser(userName);
    } else {
      console.log('No user info found in localStorage');
    }
  }, []);

  const loadBillingData = async (user = currentUser) => {
    try {
      console.log('Loading billing data for:', user);
      console.log('Date range:', startDate, 'to', endDate);

      if (!user) {
        console.log('No user provided, skipping loadBillingData');
        return;
      }

      setLoading(true);

      const formattedStartDate = startDate || getLocalDateString();
      const formattedEndDate = endDate || getLocalDateString();

      console.log('Formatted dates being sent:', formattedStartDate, 'to', formattedEndDate);

      const response = await axios.post(userTransaction, {
        currentUser: user,
        dat1: formattedStartDate,
        dat2: formattedEndDate
      });

      console.log('loadBillingData response:', response.data);

      if (response.data.success && response.data.results) {
        setBillingActivity(response.data.results);
        setPettyExpenses(response.data.pettyExpenses || []);
        console.log('Billing activity data loaded:', response.data.results.length, 'items');
        processTransactionData(response.data.results, response.data.pettyExpenses || []);
      } else {
        console.log('loadBillingData failed:', response.data.message);
        setBillingActivity([]);
        setPettyExpenses([]);
        setChartData([]);
      }
    } catch (error) {
      console.error('Error loading billing data:', error);
      setBillingActivity([]);
      setPettyExpenses([]);
      setChartData([]);
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Load Billing Data',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const processTransactionData = (data, expenses = []) => {
    if (!data || !Array.isArray(data)) {
      console.log('No valid data to process');
      return;
    }

    console.log('Processing transaction data:', data.length, 'items');

    const paymentTotals = data.reduce((acc, item) => {
      const paymentMode = item.paymentMode || 'Unknown';
      const amount      = parseFloat(item.amount)   || 0;
      const refund      = parseFloat(item.refund)   || 0;
      const reversalAmt = parseFloat(item.reversal) || 0;

      const incLower = String(item.incomeType || '').toLowerCase();
      const isReversal =
        incLower === 'reversal' ||
        incLower === 'transaction reversal' ||
        paymentMode === 'Reversed Transaction';

      // Two reversal write paths exist on day_income:
      //   • "reversal"             — inserted by the approve-reversal flow.
      //                              SQL maps the value into `amount` via
      //                              a CASE on inco_ty, so `amount > 0`.
      //   • "Transaction Reversal" — inserted by the manual reversal flow.
      //                              Writes the reversed amount to the
      //                              `reversal` column and leaves `amount`
      //                              at 0, which is why this tile used to
      //                              read 0.00 even though money had been
      //                              undone. Fall back to `reversal` so
      //                              both paths land on the right number.
      const effectiveAmount = isReversal
        ? (amount > 0 ? amount : reversalAmt)
        : amount;
      const netAmount = isReversal ? effectiveAmount : effectiveAmount - refund;

      if (!acc[paymentMode]) {
        acc[paymentMode] = 0;
      }
      acc[paymentMode] += netAmount;
      return acc;
    }, {});

    console.log('Payment totals calculated:', paymentTotals);

    const chartDataArray = Object.entries(paymentTotals)
      .filter(([mode, amount]) => amount > 0)
      .map(([mode, amount]) => ({
        name: mode,
        value: amount,
        color: getPaymentModeColor(mode)
      }))
      .sort((a, b) => b.value - a.value);

    setChartData(chartDataArray);
    console.log('Chart data set:', chartDataArray);

    const cashTotal = paymentTotals['Cash'] || 0;
    const mobileTotal = paymentTotals['Mobile Money'] || 0;
    const MobileMoneyManualTotal = paymentTotals['Mobile Money(manual)'] || 0;
    const ReversedTransactionTotal = paymentTotals['Reversed Transaction'] || 0;
    const creditTotal = paymentTotals['Credit'] || 0;
    const pettyExpensesTotal = expenses.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const totalRevenue = cashTotal + mobileTotal + MobileMoneyManualTotal;
    const totalReversal = paymentTotals['Reversed Transaction'] || 0;
    const totalExpenses = expenses.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const netRevenue = totalRevenue - totalReversal - totalExpenses;

    setSummary({ totalRevenue, totalReversal, totalExpenses, netRevenue });

    console.log('Updating stat cards with:', {
      cashTotal,
      mobileTotal,
      MobileMoneyManualTotal,
      ReversedTransactionTotal,
      creditTotal,
      pettyExpensesTotal,
      totalRevenue
    });

    setStatCards(prevCards =>
      prevCards.map(card => {
        switch (card.title) {
          case 'Cash Transactions':
            return { ...card, value: cashTotal.toFixed(2) };
          case 'Mobile Money':
            return { ...card, value: mobileTotal.toFixed(2) };
          case 'Mobile Money(manual)':
            return { ...card, value: MobileMoneyManualTotal.toFixed(2) };
          case 'Reversed Transaction':
            return { ...card, value: ReversedTransactionTotal.toFixed(2) };
          case 'Credit Transactions':
            return { ...card, value: creditTotal.toFixed(2) };
          case 'Petty Expenses':
            return { ...card, value: pettyExpensesTotal.toFixed(2) };
          case 'Total Revenue':
            return { ...card, value: totalRevenue.toFixed(2) };
          default:
            return card;
        }
      })
    );
  };

  const getPaymentModeColor = paymentMode => {
    const colors = {
      Cash: '#4caf50',
      'Mobile Money': '#2196f3',
      'Mobile Money(manual)': '#f32133ff',
      'Reversed Transaction': '#00f5f5ff',
      Credit: '#ff9800'
    };
    return colors[paymentMode] || '#757575';
  };

  const iconMap = {
    ScheduleIcon: ScheduleIcon,
    CheckCircle: CheckCircleIcon,
    WarningIcon: WarningIcon,
    AssignmentIcon: AssignmentIcon,
    CashIcon: CashIcon,
    MobileIcon: MobileIcon,
    CreditIcon: CreditIcon,
    ExpenseIcon: ExpenseIcon,
    TrendingUpIcon: TrendingUpIcon
  };

  useEffect(() => {
    if (currentUser) {
      console.log('Current user changed to:', currentUser);
      loadBillingData(currentUser);
    }
  }, [currentUser, startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const getFilteredChartData = useMemo(() => {
    console.log('Available chart data:', chartData);

    if (chartData.length === 0) {
      console.log('No chart data available');
      return [];
    }

    return chartData.slice(0, 10);
  }, [chartData]);

  const getFilteredActivityData = useMemo(() => {
    console.log('Available billing activity:', billingActivity.length, 'items');

    if (billingActivity.length === 0) {
      return [];
    }

    const sortedData = [...billingActivity].sort((a, b) => new Date(b.date) - new Date(a.date));

    console.log('Sorted billing activity data:', sortedData.length, 'items');
    return sortedData;
  }, [billingActivity]);

  const topTransactions = getFilteredChartData;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = dateString => {
    let date;
    if (typeof dateString === 'string' && dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      const parts = dateString.split('-');
      date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  const getPaymentModeChip = paymentMode => {
    // Mode → translucent tint + bright text + matching border so chips stay
    // readable on both the light and the dark theme.
    const modeColors = isDark ? {
      Cash:                    { bg: 'rgba(21,128,61,0.18)', color: '#4ade80', border: 'rgba(74,222,128,0.30)' },
      'Mobile Money':          { bg: 'rgba(25,118,210,0.18)', color: '#60a5fa', border: 'rgba(96,165,250,0.30)' },
      'Mobile Money(manual)':  { bg: 'rgba(190,24,93,0.18)',  color: '#f472b6', border: 'rgba(244,114,182,0.30)' },
      'Reversed Transaction':  { bg: 'rgba(8,145,178,0.18)',  color: '#22d3ee', border: 'rgba(34,211,238,0.30)' },
      Credit:                  { bg: 'rgba(217,119,6,0.18)',  color: '#fbbf24', border: 'rgba(251,191,36,0.30)' }
    } : {
      Cash:                    { bg: '#e8f5e8', color: '#2e7d32', border: '#a5d6a7' },
      'Mobile Money':          { bg: '#e3f2fd', color: '#1976d2', border: '#90caf9' },
      'Mobile Money(manual)':  { bg: '#fce4ec', color: '#be185d', border: '#f48fb1' },
      'Reversed Transaction':  { bg: '#e0f7fa', color: '#0891b2', border: '#80deea' },
      Credit:                  { bg: '#fff3e0', color: '#f57c00', border: '#ffcc80' }
    };

    const fallback = isDark
      ? { bg: 'rgba(255,255,255,0.06)', color: 'text.secondary', border: 'rgba(255,255,255,0.12)' }
      : { bg: '#f5f5f5', color: '#424242', border: '#e0e0e0' };
    const modeColor = modeColors[paymentMode] || fallback;

    return (
      <Chip
        label={paymentMode}
        size="small"
        sx={{
          bgcolor: modeColor.bg,
          color: modeColor.color,
          border: '1px solid',
          borderColor: modeColor.border,
          fontWeight: 700,
          fontSize: '0.72rem',
        }}
      />
    );
  };

  const getDateRangeText = () => {
    if (startDate === endDate) {
      return formatDate(startDate);
    }
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const handleDateRangeFilter = () => {
    setPage(0);
    console.log('Applying date range filter:', startDate, 'to', endDate);
    if (currentUser) {
      loadBillingData(currentUser);
    }
  };

  const setToYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayString = getLocalDateString(yesterday);
    setStartDate(yesterdayString);
    setEndDate(yesterdayString);
  };

  const PaymentModeChart = () => {
    const maxChartValue = Math.max(...topTransactions.map(item => item.value), 1);
    const accent = '#8b5cf6';

    return (
      <Card elevation={0} sx={{
        height: '100%',
        borderRadius: 2,
        border: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 1.5,
              backgroundColor: alpha(accent, isDark ? 0.18 : 0.12),
              color: accent,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <BarChartIcon />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.15, fontSize: '1.05rem' }}>
                Payment Methods
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Revenue breakdown
              </Typography>
            </Box>
          </Box>

          {topTransactions.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {topTransactions.slice(0, 5).map((item, index) => (
                <Box key={index}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.color }} />
                      <Typography sx={{ fontSize: '0.78rem', color: 'text.primary', fontWeight: 500 }}>
                        {item.name}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>
                      {formatWithOutSom(item.value)}
                    </Typography>
                  </Box>
                  <Box sx={{
                    width: '100%', height: 6, borderRadius: 3, overflow: 'hidden',
                    backgroundColor: alpha(item.color, isDark ? 0.12 : 0.10)
                  }}>
                    <Box sx={{
                      width: `${(item.value / maxChartValue) * 100}%`,
                      height: '100%', borderRadius: 3,
                      background: `linear-gradient(90deg, ${item.color} 0%, ${alpha(item.color, 0.82)} 100%)`,
                      transition: 'width 0.5s ease-in-out'
                    }} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{
              minHeight: 180,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 1
            }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: 2,
                backgroundColor: alpha(accent, 0.12),
                color: accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <BarChartIcon />
              </Box>
              <Typography variant="body2" color="text.secondary">No payment data available</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: theme => theme.zIndex.drawer + 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
        open={loading}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" color="inherit">
          Loading Dashboard...
        </Typography>
      </Backdrop>

      {/* Page header — compact, brand-styled */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 1.75,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #11998e 0%, #0e8a7f 100%)',
            color: 'white', boxShadow: '0 4px 12px rgba(17,153,142,0.35)',
          }}>
            <AccountBalanceIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.15 }}>
              Billing &amp; Accounts Dashboard
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.25 }}>
              Welcome {currentUser || '—'} · Overview for {getDateRangeText()}
            </Typography>
          </Box>
        </Box>

        {/* Date-range filter pill */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.25,
            p: 1, borderRadius: 1.75,
            border: '1px solid', borderColor: 'divider',
            bgcolor: 'background.paper',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{
            width: 30, height: 30, borderRadius: 1.25,
            bgcolor: 'rgba(17,153,142,0.16)', color: '#5eead4',
            border: '1px solid rgba(94,234,212,0.30)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <FilterIcon sx={{ fontSize: 18 }} />
          </Box>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 165 }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 165 }}
          />
          <Button
            variant="contained"
            onClick={handleDateRangeFilter}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #11998e 0%, #0e8a7f 100%)',
              boxShadow: '0 3px 10px rgba(17,153,142,0.28)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0e8a7f 0%, #0a6d63 100%)',
                boxShadow: '0 5px 14px rgba(17,153,142,0.40)',
              },
            }}
          >
            Apply Filter
          </Button>
          <Button
            variant="outlined"
            onClick={setToYesterday}
            size="small"
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: 1.5,
              borderColor: 'divider', color: 'text.primary',
              '&:hover': { borderColor: '#11998e', bgcolor: 'rgba(17,153,142,0.06)' },
            }}
          >
            Yesterday
          </Button>
        </Paper>
      </Box>


     

      {/* Row 1: six transaction stat cards — left-aligned, accent-tinted */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        {statCards.map((card, index) => {
          const IconComponent = iconMap[card.icon] || ScheduleIcon;
          return (
            <Grid item xs={6} sm={4} md={2} key={index}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, bottom: 0,
                    width: 3,
                    backgroundColor: card.accent
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 28px ${alpha(card.accent, 0.18)}`,
                    borderColor: alpha(card.accent, 0.4)
                  }
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 1.25,
                    backgroundColor: alpha(card.accent, isDark ? 0.18 : 0.12),
                    color: card.accent,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mb: 1.25
                  }}>
                    <IconComponent sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography sx={{
                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.4rem' },
                    fontWeight: 700, color: 'text.primary',
                    letterSpacing: '-0.02em', lineHeight: 1.1
                  }}>
                    {formatWithOutSom(card.value)}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.75rem', fontWeight: 700,
                    color: 'text.primary', mt: 0.5, lineHeight: 1.25
                  }}>
                    {card.title}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.68rem', color: 'text.secondary',
                    lineHeight: 1.3, mt: 0.25
                  }}>
                    {card.subtitle}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Row 2: Total Revenue hero, Accounting Summary, Payment Methods */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              height: '100%',
              color: '#fff',
              background: 'linear-gradient(135deg, #11998e 0%, #0e8a7f 60%, #0a6d63 100%)',
              boxShadow: '0 12px 28px rgba(17,153,142,0.32)',
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at top right, rgba(255,255,255,0.16), transparent 60%)',
                pointerEvents: 'none'
              }
            }}
          >
            <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 1.5,
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backdropFilter: 'blur(8px)'
                }}>
                  <TrendingUpIcon />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6, opacity: 0.9, fontWeight: 700 }}>
                    Total Revenue
                  </Typography>
                  <Typography sx={{ fontSize: '0.74rem', opacity: 0.8 }}>
                    Cash + Mobile Money
                  </Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: '2.2rem', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1 }}>
                {formatWithOutSom(summary.totalRevenue)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.18)' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.4 }}>Reversal</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>-{formatWithOutSom(summary.totalReversal)}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.4 }}>Expenses</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>-{formatWithOutSom(summary.totalExpenses)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 1.5,
                  backgroundColor: alpha('#10b981', isDark ? 0.18 : 0.12),
                  color: '#10b981',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <AssignmentIcon />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.15, fontSize: '1.05rem' }}>
                    Accounting Summary
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Revenue, reversals &amp; expenses
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  { label: 'Gross',    value: summary.totalRevenue,  sign: '',  color: 'text.primary' },
                  { label: 'Reversal', value: summary.totalReversal, sign: '-', color: '#ef4444' },
                  { label: 'Expenses', value: summary.totalExpenses, sign: '-', color: '#f59e0b' }
                ].map(row => (
                  <Box key={row.label} sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    py: 0.5
                  }}>
                    <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', fontWeight: 500 }}>{row.label}</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: row.color }}>
                      {row.sign}{formatWithOutSom(row.value)}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{
                  mt: 0.5, pt: 1.25,
                  borderTop: '1px solid', borderColor: 'divider',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <Typography sx={{
                    fontSize: '0.78rem', color: 'text.primary',
                    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6
                  }}>
                    Net
                  </Typography>
                  <Typography sx={{
                    fontSize: '1.35rem', fontWeight: 800,
                    color: summary.netRevenue >= 0 ? '#10b981' : '#ef4444',
                    letterSpacing: '-0.02em'
                  }}>
                    {formatWithOutSom(summary.netRevenue)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <PaymentModeChart />
        </Grid>
      </Grid>

      <Card elevation={0} sx={{
        borderRadius: 2,
        border: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden'
      }}>
        <Box sx={{
          px: 2.5, py: 2,
          borderBottom: '1px solid', borderColor: 'divider',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 1.5,
              backgroundColor: alpha('#11998e', isDark ? 0.18 : 0.12),
              color: '#11998e',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <ReceiptIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.02rem', color: 'text.primary', lineHeight: 1.15 }}>
                Recent Billing Activity
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getDateRangeText()} · {getFilteredActivityData.length} record{getFilteredActivityData.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Box>
          {getFilteredActivityData.length > 0 && (
            <Chip
              size="small"
              label={`Page ${page + 1} of ${Math.max(1, Math.ceil(getFilteredActivityData.length / rowsPerPage))}`}
              sx={{
                fontSize: '0.7rem', fontWeight: 700,
                backgroundColor: alpha('#11998e', isDark ? 0.18 : 0.12),
                color: '#11998e',
                border: `1px solid ${alpha('#11998e', 0.3)}`
              }}
            />
          )}
        </Box>

        {getFilteredActivityData.length > 0 ? (
          <>
            <TableContainer>
              <Table sx={{ minWidth: 900 }} aria-label="billing activity table">
                <TableHead>
                  <TableRow sx={{ '& th': {
                    bgcolor: 'action.hover',
                    borderBottom: '1px solid', borderColor: 'divider',
                    fontSize: '0.72rem', letterSpacing: '0.6px',
                    textTransform: 'uppercase', fontWeight: 700,
                    color: 'text.primary', py: 1.5
                  } }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Receipt&nbsp;ID</TableCell>
                    <TableCell>Narration</TableCell>
                    <TableCell>Income Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Reversal</TableCell>
                    <TableCell>Payment Mode</TableCell>
                    <TableCell>Patient Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredActivityData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((activity, index) => (
                      <TableRow
                        key={activity.id || index}
                        sx={{
                          '&:hover': { bgcolor: alpha('#11998e', 0.06) },
                          '& td': { borderBottom: '1px solid', borderColor: 'divider', py: 1.25 },
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell>
                          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                            {formatDate(activity.date)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#3b82f6' }}>
                            {activity.receiptId || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.85rem', color: 'text.primary' }}>{activity.narration || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', textTransform: 'capitalize' }}>
                            {activity.incomeType || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            // The manual reversal flow stores the
                            // reversed amount on the `reversal` column
                            // and writes 0 to `amount`. Fall back to
                            // `reversal` so the row displays what was
                            // actually undone instead of a misleading 0.
                            const shown = Number(activity.amount) > 0
                              ? Number(activity.amount)
                              : Number(activity.reversal) || 0;
                            const incLower = String(activity.incomeType || '').toLowerCase();
                            const isReversalRow =
                              incLower === 'reversal' ||
                              incLower === 'transaction reversal' ||
                              activity.paymentMode === 'Reversed Transaction';
                            return (
                              <Typography sx={{
                                fontSize: '0.92rem', fontWeight: 700,
                                color: isReversalRow ? '#ef4444' : '#10b981',
                              }}>
                                {isReversalRow && shown > 0 ? '-' : ''}{formatWithOutSom(shown)}
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            // Surface the reversal column (the manual
                            // reversal flow) and the refund column (the
                            // approve-reversal flow) in this column so
                            // the user can see "how much was undone"
                            // regardless of which path was used.
                            const undone = Number(activity.refund) > 0
                              ? Number(activity.refund)
                              : Number(activity.reversal) || 0;
                            return undone > 0 ? (
                              <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#ef4444' }}>
                                -{formatWithOutSom(undone)}
                              </Typography>
                            ) : (
                              <Typography sx={{ fontSize: '0.85rem', color: 'text.disabled' }}>—</Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell>{getPaymentModeChip(activity.paymentMode || 'Unknown')}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                              width: 28, height: 28, borderRadius: 1,
                              backgroundColor: alpha('#3b82f6', isDark ? 0.18 : 0.14),
                              color: '#3b82f6',
                              border: `1px solid ${alpha('#3b82f6', 0.3)}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                              <PersonOutlineIcon sx={{ fontSize: 16 }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.85rem', color: 'text.primary', fontWeight: 500 }}>
                              {activity.patientName || 'Unknown'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={getFilteredActivityData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: '1px solid', borderColor: 'divider',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.85rem', color: 'text.secondary'
                },
                '& .MuiTablePagination-select': { fontSize: '0.85rem' },
                '& .MuiIconButton-root': { color: 'text.secondary' }
              }}
            />
          </>
        ) : (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', gap: 1.5,
            minHeight: 220, py: 5
          }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: 2,
              backgroundColor: alpha('#11998e', 0.12),
              color: '#11998e',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <ReceiptIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              No billing activity found for the selected date range
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default Home;
